import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import type { Attachment } from 'app-components/flux/transactions/transactionsTypes';
import { memo } from 'react';
import type React from 'react';
import AttachmentsField from 'components/AttachmentsField';
import BoxField from 'components/Transactions/BoxField';
import VisibilitySection from 'components/Transactions/VisibilitySection';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';

import { useStyles } from '../styles';

type VisibilityDisplayState = {
  value: boolean | null | undefined;
  indeterminate: boolean;
};

type Props = {
  paymentId?: string;
  memo: string;
  setMemo: (value: string) => void;
  attachments: Attachment[] | null | undefined;
  setAttachments: (attachments: Attachment[]) => void;
  showCheckNumberField: boolean;
  checkNumber: string;
  setCheckNumber: (value: string) => void;
  disableReportsValue: boolean;
  visibilityF2SDisplay: VisibilityDisplayState;
  visibilityExcludeReportsDisplay: VisibilityDisplayState;
  setExcludeF2S: (exclude: boolean) => void;
  setExcludeReports: (exclude: boolean) => void;
  hasSplitItemsDefined: boolean;
  hasSplitTransferCounterpart: boolean;
  openSplitDialogFromExclusionsBanner: () => void;
  editSplitsFromExcludeBannerDisabled: boolean;
};

function BottomSection({
  paymentId,
  memo,
  setMemo,
  attachments,
  setAttachments,
  showCheckNumberField,
  checkNumber,
  setCheckNumber,
  disableReportsValue,
  visibilityF2SDisplay,
  visibilityExcludeReportsDisplay,
  setExcludeF2S,
  setExcludeReports,
  hasSplitItemsDefined,
  hasSplitTransferCounterpart,
  openSplitDialogFromExclusionsBanner,
  editSplitsFromExcludeBannerDisabled,
}: Props): React.JSX.Element {
  const { classes, cx } = useStyles();

  return (
    <div className={classes.bottomSection}>
      <div id='notes-and-attachments-set' className={cx(classes.column, classes.notesAndAttachmentsSetWidth)}>
        {paymentId ? (
          <TextField
            id='payment-id-field'
            label='Payment ID'
            value={paymentId}
            className={classes.paymentId}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    size='small'
                    onClick={() => {
                      navigator.clipboard.writeText(paymentId);
                      dispatchSimpleNotification('Payment ID copied to clipboard');
                    }}
                  >
                    <FileCopyOutlinedIcon fontSize='inherit' />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        ) : null}
        <BoxField
          id='memo-field'
          label='Note'
          placeholder='Add your note here'
          value={memo}
          onChange={setMemo}
          inputProps={{ maxLength: 512 }}
          margin='dense'
        />
        <AttachmentsField
          id='attachments-field'
          attachments={attachments}
          onChange={(_event: unknown, newAttachments: Attachment[]) => setAttachments(newAttachments)}
          box
          margin='dense'
        />
        {showCheckNumberField ? (
          <TextField
            label='Check #'
            value={checkNumber}
            onChange={(event) => {
              setCheckNumber(event.target.value);
            }}
            variant='filled'
          />
        ) : null}
      </div>
      <VisibilitySection
        className={classes.visibilitySection}
        disabledReports={disableReportsValue}
        excludeF2S={visibilityF2SDisplay.indeterminate ? null : visibilityF2SDisplay.value}
        setExcludeF2S={setExcludeF2S}
        indeterminateF2S={visibilityF2SDisplay.indeterminate}
        excludeReports={
          disableReportsValue
            ? true
            : visibilityExcludeReportsDisplay.indeterminate
              ? null
              : visibilityExcludeReportsDisplay.value
        }
        setExcludeReports={setExcludeReports}
        indeterminateReports={visibilityExcludeReportsDisplay.indeterminate}
        managedPerSplit={hasSplitItemsDefined}
        onEditSplits={
          hasSplitItemsDefined && !hasSplitTransferCounterpart ? openSplitDialogFromExclusionsBanner : undefined
        }
        editSplitsDisabled={
          hasSplitItemsDefined && !hasSplitTransferCounterpart ? editSplitsFromExcludeBannerDisabled : undefined
        }
      />
    </div>
  );
}

export default memo(BottomSection);
